export const SETTINGS_TAB_TYPES = Object.freeze({
  BRAND_CATEGORIES: 'brand-categories',
  DOCUMENT_TYPES: 'document-types',
  SKILLS_INSTRUCTIONS: 'skills-instructions',
})

export const BRAND_CATEGORY_FIELDS = Object.freeze([
  {
    id: 'name',
    isRequired: true,
    label: 'Name',
    placeholder: 'Home, Apparel, Fun 101 etc.',
    type: 'text',
  },
  {
    id: 'code',
    isRequired: true,
    label: 'Code',
    placeholder: 'home, apparel, fun_101',
    type: 'text',
  },
  {
    id: 'active',
    label: 'Active',
    type: 'switch',
  },
])

export const DOCUMENT_TYPE_FIELDS = Object.freeze([
  {
    id: 'displayName',
    isRequired: true,
    label: 'Display name',
    placeholder: 'Brand Guidelines, Tone of Voice etc.',
    type: 'text',
  },
  {
    hintText: 'Filled in from the display name. Edit it to use another key.',
    id: 'name',
    isRequired: true,
    label: 'Name',
    placeholder: 'BRAND_GUIDELINES, TONE_OF_VOICE',
    type: 'text',
  },
  {
    id: 'description',
    label: 'Description',
    placeholder: 'What this document type is used for',
    type: 'textarea',
  },
  {
    id: 'required',
    label: 'Required',
    type: 'switch',
  },
  {
    id: 'active',
    label: 'Active',
    type: 'switch',
  },
])

export const SKILLS_INSTRUCTIONS_FIELDS = Object.freeze([
  {
    id: 'name',
    isRequired: true,
    label: 'Name',
    placeholder: 'Skill name',
    type: 'text',
  },
  {
    hintText: 'Filled in from the name. Edit it to use another key.',
    id: 'code',
    isDisabledOnEdit: true,
    isRequired: true,
    label: 'Code',
    placeholder: 'skill_name',
    normalize: (value) => toSnakeCase(value),
    type: 'text',
  },
  {
    id: 'description',
    label: 'Description',
    placeholder: 'What this skill instruction is used for',
    type: 'textarea',
  },
  {
    id: 'dsCapability',
    label: 'DS Capability',
    isRequired: true,
    placeholder: 'What this DS capability is used for',
    type: 'text',
  },
  {
    editOnly: true,
    id: 'enabled',
    label: 'Enabled',
    type: 'switch',
  },
])

// 'Brand Guidelines' -> 'BRAND_GUIDELINES'
export const toScreamingSnakeCase = (displayName = '') =>
  `${displayName}`
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

// 'Skill Name' -> 'skill_name'
export const toSnakeCase = (displayName = '') =>
  `${displayName}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

// Each section drives one Settings tab through <ConfigurationSection />: the
// list columns, the modal fields, and the copy used for its alerts.
export const BRAND_CATEGORY_SECTION = Object.freeze({
  columns: Object.freeze([
    { id: 'label', label: 'Name', variant: 'link' },
    { id: 'code', label: 'Code' },
    { id: 'active', label: 'Status', variant: 'status' },
  ]),
  createButtonLabel: 'Create brand category',
  createSubmitLabel: 'Create category',
  emptyForm: Object.freeze({ active: true, code: '', name: '' }),
  emptyMessage: 'No brand categories have been created.',
  entityName: 'brand category',
  fields: BRAND_CATEGORY_FIELDS,
  getDisplayName: ({ name }) => name,
  listLabel: 'Brand categories',
  loadErrorHeading: 'Unable to load categories',
  loadingMessage: 'Loading brand categories…',
  savedHeading: 'Category saved',
  title: 'Brand Categories',
  toFormValues: (category) => ({
    active: category.active !== false,
    code: category.code,
    name: category.label,
  }),
})

export const DOCUMENT_TYPE_SECTION = Object.freeze({
  columns: Object.freeze([
    { id: 'label', label: 'Display name', variant: 'link' },
    { id: 'name', label: 'Name' },
    { id: 'description', label: 'Description' },
    { id: 'active', label: 'Status', variant: 'status' },
    { id: 'required', label: 'Required', variant: 'status' },
  ]),
  createButtonLabel: 'Create document type',
  createSubmitLabel: 'Create document type',
  derivedFields: Object.freeze([
    { from: 'displayName', to: 'name', transform: toScreamingSnakeCase },
  ]),
  emptyForm: Object.freeze({
    active: true,
    description: '',
    displayName: '',
    name: '',
    required: true,
  }),
  emptyMessage: 'No document types have been created.',
  entityName: 'document type',
  fields: DOCUMENT_TYPE_FIELDS,
  getDisplayName: ({ displayName }) => displayName,
  listLabel: 'Document types',
  loadErrorHeading: 'Unable to load document types',
  loadingMessage: 'Loading document types…',
  savedHeading: 'Document type saved',
  title: 'Document Types',
  toFormValues: (documentType) => ({
    active: documentType.active !== false,
    description: documentType.description ?? '',
    displayName: documentType.displayName || documentType.label,
    name: documentType.name,
    required: documentType.required === true,
  }),
})

export const SKILLS_INSTRUCTIONS_SECTION = Object.freeze({
  actionConfig: Object.freeze({
    actions: Object.freeze([
      {
        id: 'update-instructions',
        label: 'Update Skill Instruction',
      },
    ]),
  }),
  columns: Object.freeze([
    {
      id: 'code',
      label: 'Code',
      variant: 'link',
      width: 250,
    },
    {
      id: 'name',
      label: 'Name',
      width: 220,
    },
    {
      id: 'description',
      label: 'Description',
    },
    {
      id: 'dsCapability',
      label: 'DS Capability',
    },
    {
      id: 'enabled',
      label: 'Status',
      falseLabel: 'Disabled',
      trueLabel: 'Enabled',
      variant: 'status',
      width: 120,
    },
    {
      align: 'center',
      id: 'actions',
      label: 'Actions',
      variant: 'actions',
      width: 120,
    },
  ]),
  createButtonLabel: 'Create Skill',
  createSubmitLabel: 'Create Skill',
  derivedFields: Object.freeze([
    { from: 'name', to: 'code', transform: toSnakeCase },
  ]),
  emptyForm: Object.freeze({
    enabled: true,
    description: '',
    displayName: '',
    name: '',
  }),
  emptyMessage: 'No skills & instructions have been created.',
  entityName: 'skill',
  getDisplayName: ({ name }) => name,
  fields: SKILLS_INSTRUCTIONS_FIELDS,
  listLabel: 'Skills & Instructions',
  loadErrorHeading: 'Unable to load skills & instructions',
  loadingMessage: 'Loading skills & instructions…',
  savedHeading: 'Skill saved',
  title: 'Skills & Instructions',
  toFormValues: (skillInstruction) => ({
    code: skillInstruction.code,
    name: skillInstruction.name,
    description: skillInstruction.description ?? '',
    dsCapability: skillInstruction.dsCapability ?? '',
    enabled: skillInstruction.enabled !== false,
  }),
})

export const settingsTabs = Object.freeze([
  {
    id: 'brand-categories',
    label: 'Brand Categories',
    type: SETTINGS_TAB_TYPES.BRAND_CATEGORIES,
  },
  {
    id: 'document-types',
    label: 'Document Types',
    type: SETTINGS_TAB_TYPES.DOCUMENT_TYPES,
  },
  {
    id: 'skills-instructions',
    label: 'Skills & Instructions',
    type: SETTINGS_TAB_TYPES.SKILLS_INSTRUCTIONS,
  },
])
